import React, { useEffect, useRef, useState } from 'react'
import { Button, Modal, Space, Table, Upload } from 'antd'

import ViewExamineeFileModal from '../../components/ViewExamineeFileModal'
import ResponseCode from '../../common/response-code'
import { parseApplyFileList } from '../../common/apply-field-rule'
import { getUid } from '../../plugins/utils'

const fileTypeMap = {
  jpg: ['image/jpg', 'image/jpeg'],
  png: 'image/png',
  pdf: 'application/pdf',
  zip: 'application/zip',
  rar: 'application/x-rar-compressed',
  '7z': 'application/x-7z-compressed',
  doc: ['application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'],
  docx: ['application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'application/msword'],
  xls: ['application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'],
  xlsx: ['application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'],
}

const getMimeTypes = (type) => {
  const value = fileTypeMap[type]
  if (!value) {
    return []
  }
  return Array.isArray(value) ? value : [value]
}

const getFileExtension = (fileName = '') => {
  const lastDotIndex = fileName.lastIndexOf('.')
  if (lastDotIndex < 0) {
    return ''
  }
  let extension = fileName.slice(lastDotIndex + 1).toLowerCase()
  return extension === 'jpeg' ? 'jpg' : extension
}

function FileFormField ({ value, fieldItem, fileName, fieldType, uploadApi, onChange, fileUploading, fileUploaded }) {
  const viewExamineeFileModalRef = useRef()
  const [currentViewFile, setCurrentViewFile] = useState({})
  const [loading, setLoading] = useState(false)
  const [uploadResult, setUploadResult] = useState({})
  const [fileList, setFileList] = useState([])

  const isMultiFileField = fieldType === '11'
  const attachmentFormat = fieldItem?.attachmentFormat ?? ['jpg', 'png', 'pdf']
  const maxSizeKb = Number(fieldItem?.fileSize ?? 5 * 1024)

  const buildFileList = (rawValue) => {
    if (!rawValue) {
      return []
    }

    if (fieldType === '6') {
      return [{
        id: getUid(),
        fileName,
        filePath: rawValue,
        extension: getFileExtension(rawValue)
      }]
    }

    return parseApplyFileList(rawValue).map(item => {
      const currentFileName = item.fileName || String(item.filePath ?? '').split('/').pop() || ''
      return {
        ...item,
        id: item.id || getUid(),
        fileName: currentFileName,
        filePath: String(item.filePath ?? ''),
        extension: item.extension || getFileExtension(currentFileName)
      }
    })
  }

  useEffect(() => {
    setFileList(buildFileList(value))
  }, [value, fieldType, fileName])

  useEffect(() => {
    if (loading) {
      fileUploading && fileUploading()
    } else {
      fileUploaded && fileUploaded()
    }
  }, [loading])

  const showExamineeFile = (file) => {
    setCurrentViewFile(file)
    viewExamineeFileModalRef.current.showModal()
  }

  const updateFormValue = (nextList) => {
    setFileList(nextList)
    if (isMultiFileField) {
      onChange(nextList.length > 0 ? JSON.stringify(nextList) : '')
      return
    }
    onChange(nextList[0]?.filePath || '')
  }

  const deleteFile = (row) => {
    Modal.confirm({
      title: '删除文件',
      centered: true,
      content: `确定要删除文件 ${row.fileName} 吗？`,
      onOk: () => {
        updateFormValue(fileList.filter(item => item.id !== row.id))
      }
    })
  }

  const renderUploadTrigger = (buttonText) => (
    <Upload
      name={ 'file' }
      action={ uploadApi }
      headers={ {
        authorization: window.sessionStorage.getItem('access_token')
      } }
      disabled={ loading }
      maxCount={ 1 }
      showUploadList={ false }
      beforeUpload={ beforeUpload }
      onChange={ uploadFileChange }
    >
      <Button
        type={ 'primary' }
        ghost
        loading={ loading }
        size={ 'small' }
      >{ buttonText }</Button>
    </Upload>
  )

  const columns = [
    {
      title: '文件名称',
      key: 'fileName',
      dataIndex: 'fileName'
    },
    {
      title: '文件类型',
      key: 'fileType',
      dataIndex: 'extension',
      render: (extension) => extension?.toUpperCase?.() || '-'
    },
    {
      title: '操作',
      key: 'operation',
      dataIndex: 'operation',
      width: isMultiFileField ? 150 : 180,
      render: (_text, row) => {
        const extension = row.extension || ''
        return (
          <Space>
            {
              !['zip', 'rar', '7z'].includes(extension) && (
                <Button
                  type={ 'primary' }
                  ghost
                  size={ 'small' }
                  onClick={ () => { showExamineeFile(row) } }
                >查看</Button>
              )
            }
            {
              isMultiFileField
                ? (
                  <Button
                    danger
                    ghost
                    size={ 'small' }
                    onClick={ () => { deleteFile(row) } }
                  >删除</Button>
                )
                : renderUploadTrigger('重新上传')
            }
          </Space>
        )
      }
    }
  ]

  const validatePersonalPhoto = (file) => {
    if (!['image/png', 'image/jpeg'].includes(file.type)) {
      setUploadResult({ status: 0, msg: '上传失败，仅支持上传jpeg、png文件。' })
      return false
    }
    if (file.size > 300 * 1024 || file.size < 30 * 1024) {
      setUploadResult({ status: 0, msg: '上传失败，文件大小必须大于30K并且小于300K。' })
      return false
    }
    return true
  }

  const validateAttachmentFile = (file) => {
    const extension = getFileExtension(file.name)
    if (!extension) {
      Modal.error({
        title: '错误',
        content: `文件格式错误，仅支持 ${attachmentFormat.map(item => item.toUpperCase()).join('/')} 格式`
      })
      return false
    }

    const allowedMimeTypes = attachmentFormat.flatMap(type => getMimeTypes(type))
    const extensionAllowed = attachmentFormat.includes(extension)
    const mimeAllowed = allowedMimeTypes.includes(file.type)

    if (!extensionAllowed && !mimeAllowed) {
      Modal.error({
        title: '错误',
        content: `文件格式错误，仅支持 ${attachmentFormat.map(item => item.toUpperCase()).join('/')} 格式`
      })
      return false
    }

    if (file.size > maxSizeKb * 1024) {
      Modal.error({
        title: '错误',
        content: `文件大小不符合标准，单个文件大小不超过${maxSizeKb}KB`
      })
      return false
    }

    return true
  }

  const beforeUpload = (file) => {
    const isValid = isMultiFileField ? validateAttachmentFile(file) : validatePersonalPhoto(file)
    if (!isValid) {
      return Upload.LIST_IGNORE
    }
    setUploadResult({})
    setLoading(true)
    return true
  }

  const uploadFileChange = ({ file }) => {
    const res = file.response
    if (!res) {
      return
    }

    setLoading(false)
    if (res.code !== ResponseCode.success) {
      setUploadResult({ status: 0, msg: res.msg || '上传失败' })
      Modal.error({
        title: '错误',
        content: res.msg
      })
      return
    }

    setUploadResult({ status: 1, msg: '上传成功，保存后生效' })

    if (isMultiFileField) {
      const uploadedFile = {
        fieldName: fieldItem?.fieldName,
        fieldCode: fieldItem?.fieldCode,
        fileName: res.data.fileName,
        filePath: res.data.filePath,
        extension: res.data.extension || getFileExtension(res.data.fileName || res.data.filePath),
        id: getUid()
      }
      updateFormValue([...fileList, uploadedFile])
      return
    }

    updateFormValue([{
      id: getUid(),
      fileName,
      filePath: res.data.filePath,
      extension: getFileExtension(res.data.filePath)
    }])
  }

  return (<>
    <div>
      {
        isMultiFileField && (
          <>
            <div>
              <span className="text-warning">（格式：{ attachmentFormat.join('、') }）</span>
            </div>
            <div className="text-danger">{ String(fieldItem?.fieldExplain ?? '') }</div>
          </>
        )
      }

      <Table
        className="mt-2"
        bordered
        columns={ columns }
        dataSource={ fileList }
        pagination={ false }
        rowKey="id"
        locale={ { emptyText: '暂无文件' } }
      />

      {
        (isMultiFileField || fileList.length === 0) && (
          <div className="d-flex align-items-center mt-2">
            { renderUploadTrigger(isMultiFileField ? '上传文件' : '上传') }
            <span className={ `ml-2 text-${ uploadResult.status ? 'success' : 'danger' }` }>{ uploadResult.msg }</span>
          </div>
        )
      }
      {
        !isMultiFileField && fileList.length > 0 && uploadResult.msg && (
          <div className={ `mt-2 text-${ uploadResult.status ? 'success' : 'danger' }` }>{ uploadResult.msg }</div>
        )
      }
    </div>

    <ViewExamineeFileModal
      ref={ viewExamineeFileModalRef }
      fileItem={ currentViewFile }
    ></ViewExamineeFileModal>
  </>)
}

export default FileFormField
